/**
 * The words a launcher puts in front of a question to tell the engine where
 * it was asked, and the same words read back off.
 *
 * The engine is told the page only in the question's words (`AskRequest.page`
 * names a company page alone), so each launcher composes its prefix here and
 * every surface that SHOWS a question (the bubble, History, the desk's
 * Continue) strips it here. One module for both, so a prefix changed in one
 * place cannot leave the other reading the old shape. Stripping is by the
 * shape each composer writes, never by a list of page titles: a new page's
 * title strips the day it is first asked from.
 *
 * What goes to the engine keeps the prefix; only what the reader sees loses it.
 */

/** The Ask Mitra bar on any page (`MitraDock`): "About Alerts: …". */
export const aboutPage = (title: string, question: string): string => `About ${title}: ${question}`

/** A chart's own Ask tab (`chartAsk`): "About the NABIL chart: …". */
export const aboutChart = (symbol: string, question: string): string =>
  `About the ${symbol} chart: ${question}`

/** Analytics Studio's idea box (`DashboardsScreen`): "In Analytics Studio, track: …". */
export const studioTrack = (idea: string): string => `In Analytics Studio, track: ${idea}`

/** A dashboard's own Ask Mitra (`DashboardBody`): "In my “Banks” dashboard: …". */
export const inDashboard = (name: string, question: string): string =>
  `In my “${name}” dashboard: ${question}`

/** An empty dashboard's Ask Mitra (`DashboardBody`): "Add to my “Banks” dashboard: …". */
export const addToDashboard = (name: string, question: string): string =>
  `Add to my “${name}” dashboard: ${question}`

/** A question with its prefix read off: the page it names, and the bare question. */
export interface Unprefixed {
  /** The page as a short label: "Alerts", "NABIL chart", "Analytics Studio", "Banks". */
  page: string
  text: string
}

/**
 * Each composer's shape, most specific first ("About the NABIL chart: …" is
 * also an "About {title}: …"), with the label it reads back as.
 */
const SHAPES: readonly { shape: RegExp; page: (m: RegExpExecArray) => string }[] = [
  { shape: /^About the (\S+) chart: (\S[\s\S]*)$/, page: (m) => `${m[1] ?? ''} chart` },
  { shape: /^In Analytics Studio, track: (\S[\s\S]*)$/, page: () => 'Analytics Studio' },
  { shape: /^In my “([^”\n]+)” dashboard: (\S[\s\S]*)$/, page: (m) => m[1] ?? '' },
  { shape: /^Add to my “([^”\n]+)” dashboard: (\S[\s\S]*)$/, page: (m) => m[1] ?? '' },
  { shape: /^About ([^:\n]+): (\S[\s\S]*)$/, page: (m) => m[1] ?? '' },
]

/** The question without the prefix a launcher composed, or null where it carries none. */
export function unprefixed(question: string): Unprefixed | null {
  for (const { shape, page } of SHAPES) {
    const m = shape.exec(question)
    if (!m) continue
    // The question is the last group in every shape.
    const text = m[m.length - 1] ?? ''
    const label = page(m).trim()
    if (label && text) return { page: label, text }
  }
  return null
}

/** The question as the reader sees it: its prefix gone, or as it was where it had none. */
export const shownQuestion = (question: string): string => unprefixed(question)?.text ?? question
